/**
 * One body, several messages.
 *
 * A person messaging on their phone does not compose a paragraph and press send
 * once. They type a line, send it, type the next, and the thread fills up over a
 * minute or so. A device DM that arrives as one dense block is the wrong shape
 * for the medium, and the shape is the part a reader notices before they read a
 * word of it.
 *
 * So a device DM becomes a short burst of messages typed in ONE thread visit,
 * and this file is the boundary between the two views of it: the body somebody
 * wrote and approved, and the list of messages the phone types.
 *
 * WHY A DELIMITER AND NOT A SECOND COLUMN
 *
 * The obvious alternative is a `body_parts jsonb` column beside
 * `generated_content.body`. That shape stores what was said twice, and the
 * second copy is the problem rather than the column.
 *
 * `body` is already the single record of what was said, and it has a lot of
 * readers: the approval queue renders it, the pre-send duplication gate scores
 * it, the near-duplicate corpus keeps it as one of the recent hundred, the
 * inbox writes it into the thread, analytics count it, and the audit trail is
 * how anybody reconstructs what a brand actually sent a person. Add a parts
 * column and every one of those readers has to be told which copy wins. The
 * ones somebody forgets are the dangerous ones, because they keep working: a
 * reviewer edits the parts, the edit is what goes out, and the duplication gate
 * goes on scoring the original body that nobody will ever send. Nothing fails,
 * the numbers are just about a different message.
 *
 * A newline costs none of that. It is already inside the body, it is already
 * what a writer types when they mean "and then the next message", it needs no
 * migration, and every existing reader keeps reading the same column and gets
 * the same answer it got yesterday.
 *
 * WHY THE NEWLINE IS FREE TO MEAN THIS
 *
 * Because on a device channel it has never been allowed to mean anything else.
 * A device-typed message containing a line break is refused today by the
 * `not_typeable` copy rule, and that refusal is not fussiness: `adb shell input
 * text` cannot type a newline, and pressing Enter in a chat composer does not
 * insert a line, it SENDS what is in the box, so a half-written message lands
 * in front of a real person. Turning the character into a boundary can only
 * turn a refusal into a send. It cannot change what any message that already
 * went out said, because no message carrying one ever went out.
 *
 * THIS IS THE ONLY SPLITTER IN THE CODEBASE
 *
 * Generation, validation, the approval route, dispatch, the engine payload and
 * the console all ask the same question, and every one of them must ask it
 * here rather than write `body.split('\n')` at its own site. A second splitter
 * that trims differently, or keeps the empty part a trailing newline produces,
 * is not a tidiness complaint. The approval route asserts that splitting the
 * joined text gives back exactly the messages it was handed and refuses the
 * approval when it does not, so a caller splitting its own way gets a refusal
 * nobody can explain. Dispatch counts the parts against the safety ceiling, so
 * a caller splitting its own way counts a different number than the person at
 * the review desk was shown.
 */

/**
 * Any run of newlines, in either line ending, is ONE boundary.
 *
 * A run rather than a single character because a blank line between two
 * thoughts is how people separate them, and it means "next message" exactly
 * once. Splitting on a single `\n` would turn that blank line into an empty
 * third message, which on a phone is a message the composer refuses to send
 * and in a count is a part that does not exist. `\r\n` is one boundary for the
 * same reason, and it matters because copy pasted out of a Windows editor or a
 * web textarea arrives carrying them.
 */
const MESSAGE_BOUNDARY = /[\r\n]+/

/**
 * The messages a body becomes, in order.
 *
 * Each part is trimmed, because the whitespace around a line break is
 * typography rather than content and a leading space in a chat bubble is
 * visible. Parts that are empty after trimming are dropped, so leading blank
 * lines, trailing newlines and a body that is nothing but whitespace all give
 * no empty messages rather than messages a phone cannot send.
 *
 * A body with no newline in it comes back as exactly one part, which is the
 * case that keeps every message the platform sends today behaving as it does
 * today. A body that is empty or entirely whitespace comes back as NO parts:
 * there is no message in it, and returning one empty string would hand the
 * dispatch path something to type.
 */
export function splitIntoMessages(body: string): string[] {
  return body
    .split(MESSAGE_BOUNDARY)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
}

/**
 * The body that stores those messages.
 *
 * A single `\n` between parts, never a blank line, so that the text written
 * back to `generated_content.body` is the shortest one that splits back into
 * exactly these messages.
 */
export function joinMessages(parts: readonly string[]): string {
  return parts.join('\n')
}

/**
 * A connection request note as the one message it is.
 *
 * A note is a single field submitted with the request, so there is no thread to
 * put a second message in. It used to be refused when it held a line break. Now
 * every run of line breaks becomes one space, so a note written in paragraphs
 * goes out as those same words in one message. Owner decision, 2026-09-13.
 */
export function asOneMessage(body: string): string {
  return splitIntoMessages(body).join(' ')
}

/**
 * The same words, grouped into a random number of messages between one and the
 * ceiling.
 *
 * WHY. Staged DMs are written as paragraphs, and every line break is a separate
 * message on the phone. Sent as written, two hundred people get the identical five
 * message pattern, which is a shape a platform can recognise before it reads a
 * word. So the paragraphs are joined into a random number of messages, and where
 * the breaks fall is random too. Nothing is added, dropped or reordered: joining
 * the result back up gives the paragraphs in their order, separated by spaces
 * where two were merged.
 *
 * `random` is the caller's, so the choice can be seeded per person and step. The
 * same person staged twice then gets the same shape, which is what makes a retry
 * a replacement rather than a different message. Pure, because this file is also
 * bundled into the browser.
 *
 * A ceiling that is not a whole number of at least one is treated as one, which
 * joins everything. The validator refuses such a ceiling on its own, so this only
 * decides the shape of a message that is refused anyway.
 */
export function regroupMessages(body: string, ceiling: number, random: () => number): string {
  const parts = splitIntoMessages(body)
  if (parts.length <= 1) return joinMessages(parts)
  const most = Math.max(1, Math.min(parts.length, Number.isInteger(ceiling) ? ceiling : 1))
  const groups = Math.min(most, 1 + Math.floor(random() * most))

  // Pick `groups - 1` of the `parts.length - 1` gaps to break at, without repeats.
  const gaps = Array.from({ length: parts.length - 1 }, (_, index) => index + 1)
  const breaks: number[] = []
  for (let pick = 0; pick < groups - 1; pick += 1) {
    const at = Math.min(gaps.length - 1, Math.floor(random() * gaps.length))
    breaks.push(gaps[at]!)
    gaps.splice(at, 1)
  }
  breaks.sort((a, b) => a - b)

  const messages: string[] = []
  let start = 0
  for (const end of [...breaks, parts.length]) {
    messages.push(parts.slice(start, end).join(' '))
    start = end
  }
  return joinMessages(messages)
}
